// Band lookup over a committed cost-per-mile snapshot. Vertical-neutral: the
// snapshot shape is shared across verticals (boats render from
// src/constants/boats/cost-summaries.json, pets from
// src/constants/pet-shipping/cost-summaries.json), all refreshed by the unified
// update-cost-summaries workflow (one job, one PR; see
// docs/adr/0005-unified-cost-summary-refresh.md). The snapshot is the City Cost
// Summary Feed from charts.checkcitizenshipper.com — see
// docs/adr/0002-cost-data-as-committed-snapshot.md (boats, SSG) and
// docs/adr/0003-pet-cost-snapshot-under-ssr.md (pets, SSR) for why this is a
// committed file and not a live fetch. Scope fallback (city entry -> state entry
// -> null) mirrors the feed's own tier rules; thresholds are enforced
// server-side in the feed, never here.

export type CostSummaryPercentiles = {
  low: number
  typical: number
  high: number
  peak: number
}

// Typical one-way trip distance in miles for a scope. Deliberately NOT
// CostSummaryPercentiles: the feed emits p25/median/p75 for distance and adds a
// p90 ("peak") only for cost, so reusing the four-key type would invite a
// `peak` read that is always undefined. Optional on every carrier below —
// the field landed in the feed after the first committed snapshots, and a feed
// rollback must degrade to "no distance stat", not to a crash.
export type CostSummaryMileage = {
  low: number
  typical: number
  high: number
}

export type CostSummaryTrendPoint = {
  quarter: string
  label: string
  // p25 / median / p75 cost per mile for the quarter
  low: number
  typical: number
  high: number
}

export type CostSummaryTrend = {
  quarters: CostSummaryTrendPoint[]
  direction: "up" | "flat" | "down"
  percentChange: number
}

export type CostSummaryBand = {
  scope: "city" | "state"
  costPerMile: CostSummaryPercentiles
  mileage?: CostSummaryMileage | null
  trend: CostSummaryTrend | null
}

export type MarketplaceCostSummary = {
  scope: "marketplace"
  band: CostSummaryPercentiles
  // Sibling of `band`, not a key inside it — the marketplace entry flattens its
  // percentiles into `band` while city/state entries nest theirs under
  // `band.costPerMile`, so mileage sits one level up here.
  mileage?: CostSummaryMileage | null
  trend: CostSummaryTrend | null
}

type CityCostSummaryEntry = {
  city: string
  state: string
  band: CostSummaryBand | null
}

type StateCostSummaryEntry = {
  state: string
  band: CostSummaryBand
}

export type CostSummarySnapshot = {
  generatedAt: string | null
  dataWindow: { from: string; to: string } | null
  marketplace: MarketplaceCostSummary | null
  cities: ReadonlyArray<CityCostSummaryEntry>
  states: ReadonlyArray<StateCostSummaryEntry>
}

function normalize(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase()
}

// A band is renderable only when all four percentiles are finite numbers. The
// snapshot is read with an unchecked `as CostSummarySnapshot` cast, so a
// malformed committed band (null costPerMile, a missing/NaN percentile from a
// bad refresh) would otherwise reach `value.toFixed(2)` in the cost section and
// throw — on the SSR pet pages that escapes to a 500. Treating such a band as
// absent degrades to the marketplace fallback instead. Vertical-neutral; the
// boat city page renders the same percentiles inline.
export function hasFinitePercentiles(
  percentiles: CostSummaryPercentiles | null | undefined,
): percentiles is CostSummaryPercentiles {
  return (
    !!percentiles &&
    Number.isFinite(percentiles.low) &&
    Number.isFinite(percentiles.typical) &&
    Number.isFinite(percentiles.high) &&
    Number.isFinite(percentiles.peak)
  )
}

// Distance has its own guard because it has its own key count; widening
// hasFinitePercentiles would let a band with three of four cost percentiles
// through. A band whose mileage fails this still renders its cost figures — the
// distance stat is simply omitted (MAR-78: omit, never pad).
//
// "Usable" is stricter than finite. A zero or negative p25 is not a trip, and
// ordering is part of the check: p25 above the median means the feed is wrong
// about at least one of them and we cannot tell which, so "usual range
// 2,155–1,174 miles" must never reach a page.
export function hasUsableMileage(
  mileage: CostSummaryMileage | null | undefined,
): mileage is CostSummaryMileage {
  if (!mileage) {
    return false
  }

  const { low, typical, high } = mileage

  return (
    Number.isFinite(low) &&
    Number.isFinite(typical) &&
    Number.isFinite(high) &&
    low > 0 &&
    low <= typical &&
    typical <= high
  )
}

function renderableBand(band: CostSummaryBand | null): CostSummaryBand | null {
  return band && hasFinitePercentiles(band.costPerMile) ? band : null
}

export function findCostBand(
  snapshot: CostSummarySnapshot,
  city: string,
  stateCode: string,
): CostSummaryBand | null {
  const cityKey = normalize(city)
  const stateKey = normalize(stateCode)

  const cityEntry = snapshot.cities.find(
    (entry) =>
      normalize(entry.city) === cityKey && normalize(entry.state) === stateKey,
  )

  const cityBand = renderableBand(cityEntry?.band ?? null)

  if (cityBand) {
    return cityBand
  }

  const stateEntry = snapshot.states.find(
    (entry) => normalize(entry.state) === stateKey,
  )

  return renderableBand(stateEntry?.band ?? null)
}

// State-page variant: the pet-shipping state landing page has no city scope, so
// it looks up the state band directly (state entry -> null). The caller still
// falls back to the marketplace summary when this returns null, exactly like the
// city page does with findCostBand.
export function findStateCostBand(
  snapshot: CostSummarySnapshot,
  stateCode: string,
): CostSummaryBand | null {
  const stateKey = normalize(stateCode)
  const stateEntry = snapshot.states.find(
    (entry) => normalize(entry.state) === stateKey,
  )

  return renderableBand(stateEntry?.band ?? null)
}
